"use client";

// SNOWCLUB-STAGING-PAGE-LATENCY-001 · NATIVE RENDER (2026-09-22/23) — ONE RENDERER, SERVER AND CLIENT.
//
// The approved design (public/design/sg-beta-v2.01.html, byte-identical on disk) is compiled at BUILD time
// (tooling/port-harness/design-compile.mjs → src/generated/design-compiled.js; design-runtime-bundle.mjs turns
// the dc-runtime into an application module with React injected → src/generated/dc-runtime.js). DesignBundle
// renders it as a component of THIS React tree under `#dc-root`. There is no iframe, no export-document fetch,
// no template markup parsed or injected in the browser, no second React, no second root, no support.js.
//
// TWO MODES, ONE COMPONENT.
//   ssr=true  — the design is rendered on the server (react-dom/server through Next) and HYDRATED. The served
//               document carries the design's own markup (crawlers and no-JS visitors read it: the host is made
//               visible by the `html:not([data-sg-js])` rule below when no script stamps the document). Under JS
//               the host stays hidden until the boot proves the design is up (custom elements loaded, hydrated,
//               viewport measured), so no partial paint is ever exposed. First render is identical on both sides:
//               the viewport starts from the route's UA-derived guess (`vw`) and is re-measured after mount.
//   ssr=false — the previous behaviour: the design mounts client-side after the boot scripts, over the route's
//               server fallback layer. Kept for the route families not yet migrated.
//
// Three classic scripts are still loaded: the design-system bundle (reads window.React, published from the
// application's React), and the <image-slot> / <osm-map> custom elements the template mounts against. d3 /
// topojson / world-110m have one consumer — the landing world map, which polls for them — so they load after.

import * as React from "react";
import * as ReactDOMNamespace from "react-dom";
import { createRoot, hydrateRoot } from "react-dom/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MAP_STYLE_URL, sanitizeVendorStyle } from "@/lib/map-config";
import { accountsSignUpUrl, accountsUrl, walletDestination } from "@/lib/account-links";
import { designCartKey, readDesignCart, readDesignCartTip } from "@/components/marketplace/DesignCartLines";
import { cartOwnerMountain } from "@/lib/design-cart-owner";
import { cartNeedsCatalogue } from "@/lib/design-pay-order";
import type { StoreIdentityRow } from "@/lib/design-pay-order";
import { currentDesignPlan, currentDesignQuoteContext, isRejectedDesignQuote, peekSharedDesignQuote, sessionIdentityOf, sharedDesignQuote } from "@/lib/design-pay";
import type { DesignQuote } from "@/lib/design-pay";
import { designSignupPolicy } from "@/lib/design-signup-policy";
import { designFormFeedback } from "@/lib/design-form-feedback";
import { useCartStore } from "@/lib/cart-store";
import { serverAuthoredHead } from "@/lib/server-head-authority";
import URL_MAP from "@/data/export-url-map.json";
import DesignBundleChunk from "./DesignBundle";
import type { DesignEnv } from "./DesignBundle";
import EarlyPaintReveal from "./EarlyPaintReveal";
import HomepageSelectorMapLayer from "@/components/map/HomepageSelectorMapLayer";
// IMPL-004: the boot dependency list AND the readiness rule now live in ONE module, shared with
// EarlyPaintReveal.tsx's parser-first script. A found tag proves a fetch was STARTED, never that it
// finished — see that file's header for the measured false-"up" this closes.
import { DESIGN_HYDRATED_ATTR, LANDING_MAP_DEPENDENCIES, bootDependenciesReady, loadDesignScript } from "./design-script-readiness";
import {
  PAINT_POLL_MS,
  bootSucceeded,
  demoteH1sOutsideDesign,
  designMissing,
  paintVerdict,
  resolveHost,
  recoverCancelledBoot,
  settled,
  stateForVerdict,
} from "./design-restore";

declare global {
  interface Window {
    __SG_DC?: string;
    __SG_VW?: number;
    __SG_LINKS?: { wallet: string; profile: string };
    __SG_DESIGN_STATE?: "booting" | "up" | "failed";
    __SG_SERVER_HEAD?: boolean;
    __resources?: Record<string, string> | null;
    __SG_CART_QUOTE?: Record<string, unknown>;
    React?: unknown;
    ReactDOM?: unknown;
  }
}

// The compiled design + runtime is imported STATICALLY. A next/dynamic (React.lazy) child renders on the server as a
// streamed Suspense segment: the markup arrives at the END of the document in a hidden template and is moved into
// place by React's inline script — invisible to a no-JS reader and to a crawler that does not run scripts (measured
// on the served GI document: the design's h1 and 118 anchors sat in the hidden tail). Inline rendering is the point.
/** react-dom's namespace plus the client roots, published on window for the design-system bundle. */
const APP_REACT_DOM = { ...ReactDOMNamespace, createRoot, hydrateRoot };
function publishApplicationReact(): void {
  const w = window as unknown as { React?: unknown; ReactDOM?: unknown };
  w.React = React;
  w.ReactDOM = APP_REACT_DOM;
}

/** With no script stamp on <html> (JS off, or a crawler that does not execute JS) the server-rendered design is the page. */
const NO_JS_VISIBLE_CSS = "html:not([data-sg-js]) [data-sg-design-surface]{visibility:visible!important}";
const CART_QUOTE_CHANGED = "sg:cartquotechange";
const CART_QUOTE_EVENT = "sg:cartquote";
/** W17 · the slide-out notice's Try again: re-ask the quote for the cart as it is now, and nothing else hears it. */
const CART_QUOTE_RETRY = "sg:cartquoteretry";

/**
 * The design's drawer owns cart presentation; its price belongs to the existing server-priced
 * quote. This bridge listens to the design's persisted-cart change after every drawer edit and
 * replies over the same document, never deriving an amount in the browser.
 */
/**
 * FOUNDER CHECKOUT REQUEST 2026-10-27 · the top-right order summary is the ONE place the price is broken down. It is fed the server
 * quote as published here: the figures plus, when the server's own typed plan carries them, the deposit / pay-at-the-shop terms as the
 * plan states them (cents and capture plan). Nothing is computed, rounded or defaulted here; the design only formats.
 */
export function quoteSummaryPayload(q: DesignQuote): Record<string, unknown> {
  const plan = q.deposit
    ? {
        kind: "deposit",
        dueNowCents: q.deposit.dueNowCents,
        preTaxCents: q.deposit.preTaxCents,
        balanceDueCents: q.deposit.balanceDueCents,
        capture: q.deposit.capture,
      }
    : q.payment
      ? { kind: "pay_at_shop", balanceDueCents: q.payment.balanceDueCents }
      : undefined;
  return { status: "ok", ...(q.checkout ? { selectedLineIds: q.checkout.lineIds, excludedLineIds: q.checkout.excludedLineIds } : {}), subTotal: q.subTotal, tax: q.tax, fees: q.fees, total: q.total, currency: q.currency, ...(plan ? { plan } : {}) };
}

export function CartQuoteBridge({ path }: { path: string }) {
  const hostItems = useCartStore((state) => state.items);
  // The session is part of what is priced (the server picks customer vs guest pricing from it), so a login/logout/customer change
  // re-runs the effect like a cart change does.
  // The identity is the bearer token (legacy) OR the server-verified cookie-session identity generation (SSO): a cookie session has
  // no token, and guest → cookie member, a different member, or logout must re-price just as a token change does.
  const identity = useCartStore((state) => sessionIdentityOf(state));
  const request = useRef(0);

  useEffect(() => {
    void identity;
    let active = true;
    let cartKey = "";
    const publish = (answer: Record<string, unknown>) => {
      if (!active) return;
      // stamped with the cart it answers, so the design never shows or acts on an answer for a cart that has since changed
      const payload = { ...answer, cartKey };
      window.__SG_CART_QUOTE = payload;
      window.dispatchEvent(new CustomEvent(CART_QUOTE_EVENT, { detail: payload }));
    };
    const refresh = () => {
      const lines = readDesignCart(window.localStorage);
      cartKey = designCartKey(lines);
      ++request.current; // an edit invalidates any quote still in flight for the previous cart
      if (!lines.length) {
        publish({ status: "empty" });
        return;
      }
      const id = request.current;
      const catalogue = (window as unknown as { __SG_STORE?: { store?: unknown } }).__SG_STORE;
      // the live catalogue has not answered yet and this cart needs it: confirming, not refused. The design raises
      // sg:cartquotechange when the catalogue lands (success, empty or unavailable), which re-enters here with the verdict.
      if (!catalogue && cartNeedsCatalogue(lines, hostItems)) {
        publish({ status: "loading" });
        return;
      }
      const feed = catalogue?.store;
      const owner = cartOwnerMountain({ path, lines, hostItems, storeRows: Array.isArray(feed) ? (feed as StoreIdentityRow[]) : [] });
      if (!owner.ok) {
        publish({ status: "failed", reason: owner.reason });
        return;
      }
      const mountainCode = owner.mountainCode;
      const order = { tip: readDesignCartTip(window.localStorage, lines) };
      // The drawer, the cart page and the checkout card panel price ONE cart through ONE shared quote: a remount or navigation with an
      // unchanged cart shows the validated answer at once (no pending state it does not need) and never re-asks the server.
      const context = currentDesignQuoteContext(); // the non-secret pricing identity of the session this question is asked under
      const known = peekSharedDesignQuote({ mountainCode, hostItems, order });
      if (known) {
        publish(quoteSummaryPayload(known));
        return;
      }
      publish({ status: "loading" });
      void sharedDesignQuote({ mountainCode, hostItems, order }).then((quote) => {
        if (!active || id !== request.current) return;
        // The answer must be for the cart as it is NOW: the design cart, the feed and the host items can all have moved while it was
        // in flight. A mismatch is discarded and the current cart is asked again; it is never published as the current total.
        const nowPlan = currentDesignPlan({ mountainCode, hostItems: useCartStore.getState().items, order });
        // ...and for the session as it is NOW: a login/logout/customer change while pending discards ANY answer (a success or a failure).
        if (currentDesignQuoteContext() !== context || (quote.ok && (!nowPlan.ok || nowPlan.fingerprint !== quote.fingerprint))) {
          refresh();
          return;
        }
        // F1 · an in-flight answer carrying a quote the server has since rejected is never the summary's total: ask for its replacement
        if (quote.ok && isRejectedDesignQuote(quote)) {
          publish({ status: "loading" });
          window.setTimeout(() => active && id === request.current && refresh(), 1000);
          return;
        }
        publish(quote.ok
          ? quoteSummaryPayload(quote)
          : { status: "failed", reason: quote.reason, ...(quote.excludedLineIds ? { excludedLineIds: quote.excludedLineIds } : {}) });
      }).catch(() => {
        if (active && id === request.current) publish({ status: "failed", reason: "The price could not be confirmed." });
      });
    };
    refresh();
    window.addEventListener(CART_QUOTE_CHANGED, refresh);
    window.addEventListener(CART_QUOTE_RETRY, refresh);
    return () => {
      active = false;
      window.removeEventListener(CART_QUOTE_CHANGED, refresh);
      window.removeEventListener(CART_QUOTE_RETRY, refresh);
    };
  }, [hostItems, path, identity]);

  return null;
}

/**
 * SG-SITEWIDE-PERFORMANCE-RENDER-REPAIR-001-IMPL-003 · first paint. An ssr=true host already carries the complete
 * V2.01 design as server HTML (the same markup hydration reconciles against), so it is visible from the first
 * paint: no inline `visibility:hidden` waiting on the classic scripts, fonts and hydration. Only the client-mounted
 * (ssr=false) host starts hidden — there a partial client paint really would be exposed.
 */
export function hostStyle(ssr: boolean): React.CSSProperties {
  const base: React.CSSProperties = { position: "relative", width: "100%", minHeight: "100vh", background: "#fff", zIndex: 2 };
  return ssr ? base : { ...base, visibility: "hidden" };
}

// IMPL-004: this component declares NO script loader of its own. `bootDependenciesReady` (the gate
// the boot effect awaits below) and `loadDesignScript` both live in design-script-readiness.ts, so the
// readiness rule has exactly one definition and is provable without a React tree.

/**
 * The ONE place the route's server fallback layer (where a route still has one) is shown or hidden. Hidden only
 * after the design has proven it is up; shown — and the design host hidden — on every failure path.
 */
function showServerFallback(host: HTMLElement | null, show: boolean): void {
  const layers = Array.from(document.querySelectorAll<HTMLElement>("[data-sg-server-layer]"));
  if (show) {
    // An ssr host IS the readable server markup (visible from first paint) — hiding it on failure would blank the page.
    if (host && host.getAttribute("data-sg-render") !== "server") host.style.display = "none";
    for (const el of layers) {
      el.style.display = "block";
      el.removeAttribute("aria-hidden");
      el.removeAttribute("inert");
    }
    return;
  }
  for (const el of layers) {
    el.setAttribute("aria-hidden", "true");
    el.setAttribute("inert", "");
    el.style.display = "none";
  }
}

export default function DesignSurface({ dc, vw = 1440, path = "/", ssr = false, suppliers, store, children }: {
  dc: string; vw?: number; path?: string; ssr?: boolean;
  /** The mountain's supplier directory (the same JSON /api/legacy/suppliers serves), injected on the server AND before the first client render so the profile/directory screens hydrate exactly. */
  suppliers?: { supplierStatus: string; suppliers: unknown[] } | null;
  /** IMPL-010 — the live store catalogue (the same JSON /api/legacy/store serves), injected the same two-sided way for the server-rendered Booking Store; the design's own fetch guard sees it and never requests. */
  store?: unknown;
  /** Route content the design does not carry (ranked-route copy, painted blocks): rendered inside this surface, below the design, as part of the same document. */
  children?: React.ReactNode;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  /** Bumped when a restore is observed to have emptied this host; a dependency of the boot effect so it can re-enter. */
  const [bootTick, setBootTick] = useState(0);
  /** ssr=false only: the design chunk mounts after the first client render. */
  const [clientReady, setClientReady] = useState(false);
  /** One recovery boot per document (founder ruling 2026-08-19): a ref, so no re-render can reset it. */
  const recoveryUsedRef = useRef(false);
  /** Resolved by DesignBundle's mount effect (after hydration in ssr mode); awaited by the boot before "up". */
  const mountedRef = useRef<{ done: boolean; resolve: (() => void) | null }>({ done: false, resolve: null });
  const onMounted = useCallback(() => {
    // Hydration has committed: the parser-first poster and the `osm-map.js` loaders may now write into the
    // design's `<osm-map>` hosts (HYDRATION_GATED_DEPENDENCIES). Stamped BEFORE the resolve so a reader
    // woken by it already sees the marker.
    hostRef.current?.setAttribute(DESIGN_HYDRATED_ATTR, "");
    mountedRef.current.done = true;
    mountedRef.current.resolve?.();
  }, []);

  const env = useMemo<DesignEnv>(
    () => ({ dc, links: { wallet: walletDestination(true), profile: accountsUrl("/account") }, vw, path, mapStyle: MAP_STYLE_URL, suppliers: suppliers ?? undefined, store: store ?? undefined }),
    [dc, vw, path, suppliers, store],
  );
  // In the browser the logic reads these off the real window at construction (the server stand-in carries the same
  // values from `env`), so they are published BEFORE the child renders — i.e. here, not in an effect.
  if (typeof window !== "undefined") {
    window.__SG_DC = dc;
    window.__SG_VW = vw;
    window.__SG_LINKS = env.links;
    if (suppliers) (window as unknown as { __SG_SUPPLIERS?: unknown }).__SG_SUPPLIERS = suppliers; // the design's loader sees it and never fetches
    if (store) (window as unknown as { __SG_STORE?: unknown }).__SG_STORE = store; // same rule: the design's store fetch guard sees it and never requests
    (window as unknown as { __SG_MAP_STYLE?: string; __SG_MAP_TRANSFORM?: unknown }).__SG_MAP_STYLE = MAP_STYLE_URL;
    (window as unknown as { __SG_MAP_TRANSFORM?: unknown }).__SG_MAP_TRANSFORM = sanitizeVendorStyle;
    window.__SG_SIGNUP_POLICY = designSignupPolicy; // the exported sign-up reads the shared password policy from here
    window.__SG_FORM_FEEDBACK = designFormFeedback; // W17 — the exported CARD DETAILS reads the shared field feedback from here
    // Youth spec v1.1: every new account needs the age step FIRST and the served agreement, which the exported sign-up does not collect. Its
    // final action therefore creates nothing: it opens the popup, whose NEW SIGN UP is gated (SignupGate), and says why.
    // The exported design's sign-up ENTRIES (JOIN FREE, the account menu's Sign up, the auth panel's sign-up link) call this before any
    // personal field is drawn (tooling/port-harness/signup-age-gate-export-patch.mjs). Export routes mount no LoginPopup, so opening the
    // store's sign-up flag drew nothing (stg /home, 2026-10-21): the entry goes to the canonical age-first Accounts sign-up instead and
    // returns to this exact page.
    window.__SG_SIGNUP_GATE = () => window.location.assign(accountsSignUpUrl(window.location.href));
    window.__SG_SIGNUP_REGISTER = () => {
      useCartStore.getState().setAuthOpen(true);
      return Promise.resolve({ ok: false, error: "Sign up from NEW SIGN UP: SnowClub asks for your birth month and year first." });
    };
    window.__resources ??= {};
    publishApplicationReact();
  }

  useEffect(() => { setClientReady(true); }, []);

  useEffect(() => {
    // Restore repair (design-restore.ts): only an ACTUAL restore — a bfcache pageshow or a history move — and only
    // after a boot has already succeeded. A first-load pageshow must never bump (.pageshow-race-proof.mjs on 338ebfe).
    const check = () =>
      requestAnimationFrame(() => {
        if (!bootSucceeded(window.__SG_DESIGN_STATE ?? null)) return;
        const host = resolveHost(hostRef.current, typeof document === "undefined" ? null : document);
        if (designMissing(host)) setBootTick((t) => t + 1);
      });
    const onPageshow = (e: PageTransitionEvent) => { if (e.persisted) check(); };
    window.addEventListener("pageshow", onPageshow);
    window.addEventListener("popstate", check);
    return () => {
      window.removeEventListener("pageshow", onPageshow);
      window.removeEventListener("popstate", check);
    };
  }, []);

  useEffect(() => {
    // Boot guard. "booting"/"failed" -> return (strict-mode double effect; a failed boot is not looped).
    // "up" with design nodes in this host -> steady state. "up" with NONE -> re-boot (a Back restore can bring the
    // same host back with its content gone). Capped at three boots per document.
    const w = window as unknown as { __SG_DESIGN_BOOTS?: number };
    const designState = window.__SG_DESIGN_STATE;
    const host0 = hostRef.current;
    const designPresent = !!host0 && host0.querySelector("[data-dc-tpl]") !== null;
    if (designState === "booting" || designState === "failed") return;
    if (designState === "up" && designPresent) {
      // EarlyPaintReveal (ssr=true only) can win this exact "up" before this effect ever gets to run —
      // that is its whole purpose. It reveals and does the dataset/state bookkeeping directly (it is a
      // parser-first script, not React), but it does not carry the h1-demotion logic — run it here so a
      // page whose paint EarlyPaintReveal already won still gets its single-<h1> correction.
      try {
        const demoted = demoteH1sOutsideDesign(document);
        if (demoted) host0.dataset.sgH1Demoted = String(demoted);
      } catch (err) {
        console.warn("[sg-design] h1 demotion did not run:", err);
      }
      return;
    }
    if ((w.__SG_DESIGN_BOOTS ?? 0) >= 3) {
      console.warn("[sg-design] re-boot cap reached; leaving the server-rendered layer in place");
      showServerFallback(host0, true);
      return;
    }
    w.__SG_DESIGN_BOOTS = (w.__SG_DESIGN_BOOTS ?? 0) + 1;
    window.__SG_DESIGN_STATE = "booting";
    // A server-emitted canonical OR robots directive means the server authored this head (@/lib/server-head-authority).
    window.__SG_SERVER_HEAD = serverAuthoredHead(document);

    let cancelled = false;

    (async () => {
      const host = hostRef.current;
      if (!host) throw new Error("design host element is not mounted");
      publishApplicationReact();
      // The design chunk is rendering (hydrating in ssr mode) in parallel; its mount effect resolves this.
      const hydrated = new Promise<void>((resolve) => {
        if (mountedRef.current.done) resolve();
        else mountedRef.current.resolve = resolve;
      });
      // On an ssr host, `osm-map.js` is not started until hydration has committed (IMPL-008): upgrading an
      // `<osm-map>` mid-hydration adds children React never rendered, and hydration fails (#418).
      const bootDependencies = bootDependenciesReady(undefined, undefined, ssr ? hydrated : undefined);
      bootDependencies.catch(() => undefined); // observed at `await bootDependencies` below
      await hydrated;
      await bootDependencies;
      if (cancelled) return; // a cancelled boot leaves no state behind (recoverCancelledBoot)

      void Promise.all(LANDING_MAP_DEPENDENCIES.map((src) => loadDesignScript(src))).catch((error) => {
        console.warn("[sg-design] landing map dependency failed to load; the page is booted, the map is not drawn", error);
      });

      // "up" means PAINTED (founder ruling 2026-08-19): a rendered template node — the runtime's own marker — in the host.
      const verifyStart = Date.now();
      let verdict = paintVerdict(host);
      while (!settled(verdict, Date.now() - verifyStart)) {
        await new Promise((r) => setTimeout(r, PAINT_POLL_MS));
        if (cancelled) return;
        verdict = paintVerdict(host);
      }
      const state = stateForVerdict(verdict);

      // One bounded, single-flight recovery boot before declaring failure.
      if (state !== "up" && !recoveryUsedRef.current) {
        recoveryUsedRef.current = true;
        console.warn(
          `[sg-design] no rendered template after ${Date.now() - verifyStart}ms (verdict=${verdict}) — one recovery boot`,
        );
        delete (window as { __SG_DESIGN_STATE?: unknown }).__SG_DESIGN_STATE;
        mountedRef.current = { done: false, resolve: null };
        setBootTick((t) => t + 1);
        return;
      }

      window.__SG_DESIGN_STATE = state;
      host.dataset.sgDesign = state;
      host.dataset.sgPaint = verdict;
      if (state !== "up") {
        console.error(
          `[sg-design] no rendered template after the recovery boot (verdict=${verdict}, waited ${Date.now() - verifyStart}ms) — state=failed, staying failed`,
        );
        showServerFallback(host, true);
        return;
      }
      // Reveal only once the complete design is proven up (hydrated, custom elements loaded, viewport measured).
      host.style.visibility = "visible";
      showServerFallback(host, false);
      // One rendered <h1> per page (SEO-CUTOVER W3): every <h1> outside the design is demoted to a <p>.
      try {
        const demoted = demoteH1sOutsideDesign(document);
        if (demoted) host.dataset.sgH1Demoted = String(demoted);
      } catch (err) {
        console.warn("[sg-design] h1 demotion did not run:", err);
      }
    })().catch((err) => {
      // Fail visibly in the log, gracefully on screen: the server-rendered page (in ssr mode the design's own
      // server markup, revealed by the stamp script's stall path), not a white rectangle.
      window.__SG_DESIGN_STATE = "failed";
      showServerFallback(hostRef.current, true);
      console.error("[sg-design] the founder's design did not mount:", err);
    });

    return () => {
      cancelled = true;
      if (window.__SG_DESIGN_STATE === "booting") {
        recoverCancelledBoot(w, hostRef.current, () => setBootTick((t) => t + 1));
      }
    };
  }, [dc, bootTick, ssr]);

  // WO-URL-NAV-001 · NAV-4: placeholder hrefs ("#"/missing) get their CONTRACT path under the current mountain,
  // written ONLY if that exact path exists in export-url-map — derivation + verification, never invention.
  useEffect(() => {
    const KNOWN = new Set((URL_MAP as unknown as { entries: [string, string, ...string[]][] }).entries.map((r) => r[1]));
    const mtn = /^\/(mountain-[a-z0-9-]+)(?:\/|$)/.exec(window.location.pathname)?.[1] ?? null;
    const slug = (s: string) => s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    const suffixFor = (label: string): string | null => {
      const l = label.replace(/\s+/g, " ").trim();
      let m: RegExpExecArray | null;
      if ((m = /^(.+) to (.+)$/i.exec(l))) return `${slug(m[1] ?? "")}-to-${slug(m[2] ?? "")}`;
      if (/resort profile$/i.test(l)) return "profile";
      if (/^(.+) activities$/i.test(l)) return "activities";
      if (/^ski rentals at /i.test(l)) return "ski-rentals";
      if (/member deals$/i.test(l)) return "deals-and-packages";
      if (/^snowclub perks$/i.test(l)) return "snowclub-perks";
      if (/^gateway bus loop$/i.test(l)) return "gateway-bus-loop";
      if (/^yvr airport transfers$/i.test(l)) return "bus-schedule/airport-information";
      if ((m = /^(.+) bus$/i.exec(l))) return `${slug(m[1] ?? "")}-bus`;
      return null;
    };
    const assign = () => {
      const root = document.getElementById("dc-root");
      if (!root) return;
      for (const a of root.querySelectorAll<HTMLAnchorElement>("a")) {
        const href = a.getAttribute("href");
        if (href !== null && href !== "" && href !== "#") continue;
        if (a.dataset.sgHrefRestored) continue;
        const label = (a.textContent || "").replace(/\s+/g, " ").trim();
        if (!label || !mtn) continue;
        const suffix = suffixFor(label);
        if (!suffix) continue;
        const p = `/${mtn}/${suffix}`;
        if (!KNOWN.has(p)) continue; // not in the contract — never invent
        a.setAttribute("href", p);
        a.dataset.sgHrefRestored = "1";
      }
    };
    assign();
    const mo = new MutationObserver(() => assign());
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  // NAV-1 (founder ruling 2026-08-18): NO click interception here. Real anchors navigate full-document
  // because the export's own anchor cancellation is patched at source (design-sources.ts).

  return (
    <>
      <CartQuoteBridge path={path} />
      <link rel="preload" as="script" href="/design/_ds/snowgroup-design-system-6d9a4167-d513-4f87-84d5-03d3dc7c5420/_ds_bundle.js" />
      <link rel="preload" as="script" href="/design/image-slot.js" />
      <link rel="preload" as="script" href="/design/osm-map.js" />
      {ssr ? <style data-sg-design-nojs="">{NO_JS_VISIBLE_CSS}</style> : null}
      <div
        ref={hostRef}
        data-sg-design-surface=""
        data-sg-nav="native"
        data-sg-render={ssr ? "server" : "client"}
        // In normal flow (QA-BATCH-01 rows 6/9): the design's own content extends the document, which is the one scroller.
        style={hostStyle(ssr)}
      >
        {ssr || clientReady ? <DesignBundleChunk key={bootTick} env={env} onMounted={onMounted} /> : null}
        {children}
      </div>
      {/* SNOWCLUB-MOUNTAIN-PICKER-MAP-STAGING-20261025 — the header dropdown's inline map pane
          (section[data-sg-menu="mtn"] [data-mtn-map-pane]) becomes the homepage SVG map. Mounted HERE, beside the
          host and outside the design's own DOM, because several roots render DesignSurface directly and bypass
          ExportMain (page.tsx's mountain landing); one surface per document means exactly one bridge. The layer
          renders nothing and attaches only to that section (never the transport map). */}
      <HomepageSelectorMapLayer />
      {ssr ? <EarlyPaintReveal /> : null}
    </>
  );
}
